import { Role, SubscriptionStatus } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@utima/ui';
import { Wrench, CreditCard } from 'lucide-react';

import { Card } from '@/components/card/Card';
import { FormPage } from '@/components/page/FormPage';
import { Protected } from '@/components/protected/Protected';
import {
  OrganizationForm,
  OrganizationUsageReport,
  OverrideLimitsForm,
  PartnerManagementCard,
  ResetMonthlyUsage,
} from '@/features/organizations';
import { useUser } from '@/hooks';
import { isPartnerCapable } from '@/lib/routerUtils';
import { trpc } from '@/services/trpc';

const populate = ['billingInfo', 'workingHours', 'subscriptions', 'tierConfig'];

export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/edit',
)({
  component: OrganizationsEditPage,
  beforeLoad: async ({
    context: { accessGuard },
    params: { organizationId },
  }) => accessGuard([Role.SuperAdmin, Role.Admin], organizationId),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) => {
    await Promise.all([
      trpcUtils.organizations.findOne.ensureData({
        id: organizationId,
        populate,
      }),
      trpcUtils.subscriptions.getActive.ensureData({ organizationId }),
    ]);
  },
});

export function OrganizationsEditPage() {
  const { organizationId } = Route.useParams();
  const { t } = useLingui();
  const { user, currentOrganizationId } = useUser();
  const isSuperAdmin = user?.role === Role.SuperAdmin;
  const partnerCapable = isPartnerCapable(user);
  const isOwnOrg = organizationId === currentOrganizationId;
  const [data] = trpc.organizations.findOne.useSuspenseQuery({
    id: organizationId,
    populate,
  });

  const currentSubscription = data.subscriptions.find(
    sub => sub.status === SubscriptionStatus.Active,
  );

  return (
    <FormPage
      title={`${t`Edit Organization`} - ${data.name}`}
      breadcrumbs={[
        {
          label: t`Organizations`,
          to: '/organizations',
        },
        {
          label: data.name,
          to: '/organizations/$organizationId/edit',
          params: { organizationId },
        },
      ]}
      actions={
        <>
          {!isSuperAdmin && isOwnOrg && (
            <Button
              data-cta='admin-organizations-edit-self-serve-payments-button'
              variant='secondary'
              asChild
            >
              <Link
                to='/organizations/$organizationId/payments'
                params={{ organizationId }}
              >
                <CreditCard className='size-4' />
                <Trans>Payments</Trans>
              </Link>
            </Button>
          )}
          {!isSuperAdmin && partnerCapable && !data.isPartnerOrganization && (
            <Button
              data-cta='admin-organizations-edit-partner-billing-button'
              variant='primary'
              asChild
            >
              <Link
                to='/partner-billing/$organizationId'
                params={{ organizationId }}
              >
                <Wrench className='size-4' />
                <Trans>Plan & billing management</Trans>
              </Link>
            </Button>
          )}
          <Protected roles={[Role.SuperAdmin]}>
            <Button
              data-cta='admin-organizations-edit-manage-custom-plan-button'
              variant='primary'
              asChild
            >
              <Link
                to='/organizations/$organizationId/plan-management'
                params={{ organizationId }}
              >
                <Wrench className='size-4' />
                <Trans>Plan & billing management</Trans>
              </Link>
            </Button>
            <Button
              data-cta='admin-organizations-edit-manage-payments-button'
              variant='secondary'
              asChild
              icon
            >
              <Link
                to='/organizations/$organizationId/payments'
                params={{ organizationId }}
              >
                <CreditCard className='size-4' />
                <Trans>Manage Payments</Trans>
              </Link>
            </Button>
          </Protected>
        </>
      }
    >
      <OrganizationForm organizationId={organizationId} data={data} />

      {data && (
        <Protected roles={[Role.SuperAdmin]}>
          <Card title={t`SuperAdmin Tools`} variant='danger' className='mt-6'>
            <div className='grid grow grid-cols-1 gap-6 xl:grid-cols-3'>
              {currentSubscription && (
                <OverrideLimitsForm
                  organizationId={data.id}
                  currentSubscription={currentSubscription}
                />
              )}
              <ResetMonthlyUsage organizationId={data?.id} />
            </div>
            <PartnerManagementCard clientOrganizationId={organizationId} />
            <OrganizationUsageReport
              organizationId={organizationId}
              currency={data.currency}
            />
          </Card>
        </Protected>
      )}
    </FormPage>
  );
}
